import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/forms';
import { Modal } from '../../ui/Modal';
import { ActorName, Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/**
 * IDN-06: oversight of every AI agent. Personal agents come from people's browser sign-ins and mirror their
 * owner's access; headless agents use admin-issued credentials and per-project grants.
 */
export function AiServicesPage() {
  usePageTitle('AI agents');
  useCrumbs([{ label: 'Administration' }, { label: 'AI agents' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['ai-services'],
    queryFn: () => api.get<AiServiceSummary[]>('/admin/ai-services'),
    enabled: admin,
  });
  const [creating, setCreating] = useState(false);
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="AI agents"
        subtitle="Agents work through the lixionary-jira plugin for Claude Code, Cursor or Antigravity. None of them can approve work, releases or rollbacks."
        actions={
          <Button variant="secondary" onClick={() => setCreating(true)}>
            Add headless agent
          </Button>
        }
      />
      <QueryView query={q}>
        {(list) => {
          const personal = list.filter((s) => s.owner);
          const headless = list.filter((s) => !s.owner);
          return (
            <div className="stack">
              <Card
                title="Personal agents"
                subtitle="Created when a person signs in with the lixionary-jira plugin from Claude Code, Cursor or Antigravity. Each one reads and writes exactly its owner's projects."
              >
                {personal.length === 0 ? (
                  <EmptyState icon={Bot} title="No personal agents yet">
                    People connect their agent by installing the plugin and signing in. See My AI connections.
                  </EmptyState>
                ) : (
                  <AgentTable rows={personal} personal />
                )}
              </Card>
              <Card
                title="Headless agents"
                subtitle="For agents that run without a person, such as CI builders. They read all projects and write only where granted."
              >
                {headless.length === 0 ? (
                  <span className="small muted">No headless agents.</span>
                ) : (
                  <AgentTable rows={headless} />
                )}
              </Card>
            </div>
          );
        }}
      </QueryView>
      {creating && <AddHeadlessAgent onClose={() => setCreating(false)} />}
    </div>
  );
}

function AgentTable({ rows, personal }: { rows: AiServiceSummary[]; personal?: boolean }) {
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/admin/ai-agents/${id}`);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Agent</th>
            {personal && <th>Owner</th>}
            <th>State</th>
            <th className="num">{personal ? 'Active connections' : 'Active credentials'}</th>
            <th>Last used</th>
            {!personal && <th>Write grants</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="clickable" onClick={() => open(s.id)}>
              <td>
                <a
                  href={`/admin/ai-agents/${s.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    open(s.id);
                  }}
                  className="primary-cell"
                >
                  {s.name}
                </a>
                {!personal && s.description && <div className="small muted">{s.description}</div>}
              </td>
              {personal && (
                <td className="small">
                  <ActorName actor={s.owner} />
                </td>
              )}
              <td>{s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</td>
              <td className="num">{s.activeCredentials}</td>
              <td className="small">
                <Timestamp value={s.lastUsedAt} relative />
              </td>
              {!personal && (
                <td className="small">
                  {s.grants.length ? (
                    s.grants.map((g) => `${g.projectKey} (${g.capabilities})`).join(', ')
                  ) : (
                    <span className="muted">None</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddHeadlessAgent({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useAction(() => api.post<{ id: string }>('/admin/ai-services', { name, description }), {
    success: 'Headless agent added',
    invalidate: [['ai-services']],
    onSuccess: (r) => navigate(`/admin/ai-agents/${r.id}`),
  });
  return (
    <Modal
      title="Add headless agent"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate(undefined)} loading={create.isPending} disabled={!name}>
            Add agent
          </Button>
        </>
      }
    >
      <p className="small muted">
        For agents that run unattended. People don't need this: their agent is created when they sign in.
      </p>
      <Field label="Name" hint="Shown as the actor on comments, claims and evidence.">
        {(p) => (
          <Input
            {...p}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ci-builder"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Description" optional>
        {(p) => (
          <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <p className="small muted">
        Next, issue it a credential and grant project capabilities. New agents have no write grants.
      </p>
      <ErrorAlert error={create.error} />
    </Modal>
  );
}
